import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import {
  ArrowRight, Award, Battery, Bike, CheckCircle2, Clock,
  Factory, Globe, Leaf, Shield, TrendingUp, Zap
} from 'lucide-react';
import { useInView } from '../components/UI';
import { blogPosts } from '../data/blogPosts';
import WhitelistForm from '../components/WhitelistForm';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const YOUTUBE_VIDEO_ID = 'M7lc1UVf-VE'; // TODO: swap for the real Azura Mobility video ID

const tickerItems = [
  'Fleet Operations', 'Battery-as-a-Service', 'Electric Ride-Hailing',
  'Sankofa Foundry', 'Carbon Finance', 'AZT Token · $0.005', '500 Bikes · 2026',
];

const services = [
  { num: '01', title: 'Electric Fleet Operations', desc: 'A proven $6/day payment. 312 working days a year. $1,872 a year in earnings per bike, with digital payments and real-time tracking.', icon: <Bike size={20} />, link: '/ecosystem' },
  { num: '02', title: 'Battery-as-a-Service', desc: 'Swap stations that take under two minutes per exchange. No need for the power grid. 2.0 swaps per bike per day, with walk-in income at $1.20/swap.', icon: <Battery size={20} />, link: '/ecosystem' },
  { num: '03', title: 'Electric Ride-Hailing', desc: 'Azura Ride launches Phase 2 built directly on the battery-swap network: lower cost, cleaner, and a long-term advantage no competitor can copy.', icon: <Globe size={20} />, link: '/ecosystem' },
  { num: '04', title: 'Sankofa Foundry', desc: "A step-by-step plan to become Africa's first fully local vehicle manufacturer. Assembling vehicles from imported parts starting 2033. Full local manufacturing by 2042. Profit margin rises from 16.7% to 41.2%.", icon: <Factory size={20} />, link: '/sankofa' },
  { num: '05', title: 'Carbon Finance', desc: 'Every EV we put on the road earns carbon credits that can be registered and sold. Qualifies for Gold Standard and Verra certification from day one.', icon: <Leaf size={20} />, link: '/climate-impact' },
];

const advantages = [
  'Affordable EVs designed for everyday African use',
  'Battery swaps completed in under two minutes',
  'Built for African roads, cities, and economics',
  'Lower running costs than any petrol alternative',
  'Battery-swap network grows stronger with every new phase',
  'Operator today, local manufacturer by 2042',
];

const validations = [
  { text: 'Ogun State rolled out 2,000 electric motorcycles with a $40M investment plan in 2024, showing strong state-level commitment to EV adoption.', source: 'Ogun State Government', icon: <Award size={14} /> },
  { text: 'Electric two-wheeler sales in Africa grew 40% year over year in 2024. In Kenya, e-motos reached 15.3% of new motorcycle registrations in 2025.', source: 'Africa E-Mobility Report 2025', icon: <TrendingUp size={14} /> },
  { text: "Nigeria projects that over 60% of two-wheelers will be electric by 2040. Government policy is strongly supporting this shift.", source: 'Nigerian EV Policy Framework', icon: <Shield size={14} /> },
];

const marketNums = [
  { v: '$3.78B', l: 'Africa Moto Market 2024' },
  { v: '+40%', l: 'E-Moto Yearly Growth 2024' },
  { v: '15.3%', l: 'Kenya E-Moto Share 2025' },
  { v: '60%+', l: 'Nigeria EV Target 2040' },
];

const bottomStats = [
  { value: '500+', label: 'First EVs on the Road' },
  { value: '2.0x', label: 'Daily Battery Swaps' },
  { value: '$1.8k+', label: 'Yearly Earnings / Bike' },
  { value: '2042', label: 'Full Manufacturing Target Year' },
];

/* ══ HOME ══ */
const Home: React.FC = () => {
  const words = ['Clean Transport', 'Electric Mobility', 'Green Infrastructure'];
  const [wordIdx, setWordIdx] = useState(0);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    let ci = 0;
    let t: ReturnType<typeof setTimeout>;
    const w = words[wordIdx];
    const type = () => {
      if (ci <= w.length) { setTyped(w.slice(0, ci)); ci++; t = setTimeout(type, 62); }
      else { t = setTimeout(() => setWordIdx(i => (i + 1) % words.length), 2300); }
    };
    type();
    return () => clearTimeout(t);
  }, [wordIdx]); // eslint-disable-line

  const { ref: svcRef, inView: svcInView } = useInView();
  const { ref: advRef, inView: advInView } = useInView();
  const { ref: aboutRef } = useInView();
  const { ref: blogRef, inView: blogInView } = useInView();
  const featuredPosts = blogPosts.slice(0, 3);

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative">
        <div className="absolute -bottom-20 -left-20 w-72 h-72 border-[20px] border-bg-dark opacity-[0.04] rounded-full pointer-events-none" />
        <div className="absolute bottom-10 left-1/3 w-56 h-20 opacity-[0.18] pointer-events-none" style={{ background: 'radial-gradient(#14406f 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        
        {[
          { top: '18%', left: '42%', size: 6, delay: 0 },
          { top: '72%', left: '38%', size: 4, delay: 0.6 },
          { top: '30%', right: '8%', size: 5, delay: 1.1 },
        ].map((s, i) => (
          <motion.div key={i} animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.5, repeat: Infinity, delay: s.delay, ease: 'easeInOut' }}
            className="absolute pointer-events-none"
            style={{ top: s.top, left: 'left' in s ? s.left : undefined, right: 'right' in s ? (s as any).right : undefined }}>
            <svg width={s.size * 2} height={s.size * 2} viewBox="0 0 24 24" fill="#FFB800">
              <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
            </svg>
          </motion.div>
        ))}

        <div className="px-6 md:px-12 pt-[100px] md:pt-[140px] pb-20 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[calc(100vh-72px)] overflow-hidden">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="lg:col-span-6 z-10">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 border border-dashed border-bg-dark/30 bg-accent-amber/10 px-4 py-1.5 rounded-lg mb-7">
              <Zap size={11} className="text-accent-amber" />
              <span className="text-xs font-bold uppercase text-bg-dark">Welcome to Azura Mobility</span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="font-figtree font-black leading-[1.06] tracking-tight text-bg-dark mb-6 text-4xl sm:text-5xl md:text-[3.2rem] lg:text-[3rem] xl:text-[3.5rem]">
              Building Africa's<br />
              <span className="relative inline-block">
                <span className="text-accent-amber">{typed}</span>
                <span className="text-accent-amber animate-pulse">|</span>
                <svg className="absolute -bottom-1 left-0 w-full overflow-visible" viewBox="0 0 300 6" fill="none" preserveAspectRatio="none" style={{ height: 6 }}>
                  <path d="M0 4 Q75 0 150 4 Q225 8 300 4" stroke="#FFB800" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5" />
                </svg>
              </span>
              <br />for the future
            </motion.h1>

            <motion.p variants={fadeUp} className="font-exo text-neutral-600 leading-relaxed text-base md:text-base mb-8 max-w-lg">
              Azura Mobility is a fully integrated electric vehicle network: fleet operations, Battery-as-a-Service, ride-hailing, and Africa's first local EV manufacturing capability.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 md:mb-10">
              <Link to="/invest" className="inline-flex items-center gap-2.5 bg-bg-dark text-white px-6 py-3.5 rounded-full font-figtree font-bold text-xs tracking-wider uppercase hover:bg-bg-dark-mid transition-all shadow-capsule group">
                Invest Now
                <span className="w-6 h-6 rounded-full bg-accent-amber flex items-center justify-center text-bg-dark group-hover:scale-110 transition-transform">
                  <ArrowRight size={12} />
                </span>
              </Link>
              <a href="#whitelist"
                className="inline-flex items-center gap-2 border border-bg-dark/25 bg-white text-bg-dark px-6 py-3.5 rounded-full font-figtree font-bold text-xs tracking-wider uppercase hover:border-bg-dark transition-all">
                White Paper ↓
              </a>
            </motion.div>
{/* 
            <motion.div variants={fadeUp} className="flex gap-8 pt-6 border-t border-border">
              {[['500', 'EVs Phase 1'], ['$6/day', 'Validated'], ['2042', 'Full OEM']].map(([v, l]) => (
                <div key={l}>
                  <p className="font-figtree font-black text-2xl text-bg-dark">{v}</p>
                  <p className="text-neutral-500 font-exo text-xs mt-0.5">{l}</p>
                </div>
              ))}
            </motion.div> */}
          </motion.div>

          <div className="lg:col-span-6 relative flex justify-center items-center min-h-[420px]">
            <div className="absolute inset-4 bg-accent-amber opacity-[0.12] pointer-events-none" style={{ borderRadius: '42% 58% 38% 62% / 55% 42% 58% 45%', transform: 'rotate(-6deg)' }} />
            <div className="absolute w-[70%] aspect-square rounded-full border border-bg-dark/8 pointer-events-none" />

            <motion.div initial={{ opacity: 0, scale: 0.92, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
              className="relative z-10 md:w-[90%] rounded-[2rem] overflow-hidden shadow-[0_32px_80px_rgba(30,58,47,0.18)] border border-neutral-200 bg-white p-2">
              <img src="/images/bg4.jpg" alt="Azura" className="w-full h-[400px] object-cover rounded-[1.7rem]"/>
              <div className="absolute inset-2 rounded-[1.7rem] bg-gradient-to-t from-bg-dark/40 via-transparent to-transparent pointer-events-none" />
            </motion.div>

            <motion.div animate={{ y: [0, -7, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-12 -right-3 z-20 bg-bg-dark text-white text-xs font-figtree font-bold tracking-widest uppercase px-4 py-2 rounded-full shadow-capsule border border-neutral-700/30 flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-accent-amber animate-pulse" /> Azura
            </motion.div>

            <div className="absolute bottom-6 right-8 z-10 flex flex-col gap-1.5 pointer-events-none">
              <motion.svg animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} width="18" height="18" viewBox="0 0 24 24" fill="#FFB800">
                <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
              </motion.svg>
              <motion.svg animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }} className="translate-x-3" width="12" height="12" viewBox="0 0 24 24" fill="#FFB800">
                <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
              </motion.svg>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TICKER ══ */}
      <div className="w-full bg-accent-amber py-1 border-y-1 border-bg-dark overflow-hidden whitespace-nowrap">
        <div className="flex animate-ticker">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="flex items-center mx-12 text-xs font-figtree font-black tracking-widest text-bg-dark uppercase">
              {item} <span className="mx-6 text-xl font-light text-bg-dark/40 font-serif">∗</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══ ABOUT ══ */}
      <section className="py-24 px-6 bg-white border-t border-border overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-20">
            <div ref={aboutRef} className="lg:col-span-7 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/50">About Azura</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-figtree font-black text-bg-dark tracking-tight max-w-2xl mb-4">
                The Azura <span className="text-accent-amber">Idea</span>
              </h2>

              <p className="font-exo text-neutral-600 text-base leading-relaxed mb-4 max-w-xl">
                Azura Mobility is a Nigerian electric vehicle company founded in Ibadan in 2026. Rather than copying a Western EV model, Azura was designed from the ground up: starting with the daily costs and earnings of a commercial motorcycle rider and building a complete network around solving their specific problems.
              </p>
              <p className="font-exo text-neutral-600 text-base leading-relaxed mb-8 max-w-xl">
                Nigeria's fuel subsidy removal in 2023 pushed fuel costs to 30–50% of daily rider income. Regular EV charging (4–8 hours) makes electric alternatives too expensive to run as a business, and Africa's 25 million motorcycles are made almost entirely outside the continent. Azura tackles all three problems at once.
              </p>

              <Link to="/about" className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3.5 rounded-full font-figtree font-bold text-xs tracking-wider uppercase hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule group self-start">
                Learn More <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="lg:col-span-5 grid grid-cols-1 gap-4 relative">
              <div className="absolute top-1/2 -right-8 -translate-y-1/2 hidden lg:flex flex-col gap-2 opacity-20 pointer-events-none z-0">
                <div className="w-16 h-8 rounded-t-full border-t-2 border-x-2 border-bg-dark" />
                <div className="w-16 h-8 rounded-b-full border-b-2 border-x-2 border-bg-dark" />
              </div>

              <div className="rounded-[2rem] overflow-hidden shadow-card border border-border z-10">
                <img src="/images/bg3.jpg" alt="Azura Operations" className="w-full h-auto"
                 />
              </div>

              <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -left-4 bg-white border border-border shadow-card rounded-2xl px-4 py-3 z-20 hidden lg:block">
                <p className="font-figtree font-black text-xl text-bg-dark leading-none">Africa</p>
                <p className="text-xs text-neutral-500 mt-0.5 tracking-widest uppercase">to the world</p>
              </motion.div>
            </div>
          </div>

          <div className="border-t border-border pt-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {bottomStats.map((stat, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="py-2">
                    <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      className="font-figtree font-black text-3xl md:text-4xl text-bg-dark tracking-tight mb-1">
                      {stat.value}
                    </motion.p>
                    <p className="text-xs font-exo text-neutral-500 leading-tight">{stat.label}</p>
                  </div>
                  {idx !== bottomStats.length - 1 && (
                    <div className="hidden md:flex items-center h-12 relative pl-6 opacity-40">
                      <div className="w-[1px] h-full bg-neutral-300" />
                      <div className="flex items-center -space-x-1 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-accent-amber" />
                        <div className="w-2.5 h-2.5 rounded-full bg-bg-dark" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ MISSION ══ */}
      <section className="py-24 px-6 bg-bg-dark overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full border border-white/[0.05]" />
          <div className="absolute -top-12 -right-12 w-[280px] h-[280px] rounded-full border border-white/[0.04]" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-accent-amber opacity-[0.06]" style={{ borderRadius: '60% 40% 55% 45% / 50% 60% 40% 55%' }} />
          <div className="absolute bottom-10 right-16 w-40 h-24 opacity-[0.12]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Our Mission</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-white leading-[1.08] tracking-tight">
                To power Africa's transportation revolution
                <span className="text-accent-amber italic"> without repeating the past.</span>
              </h2>
            </div>
{/*
            <div className="lg:col-span-7 flex flex-col justify-center lg:pt-4 lg:pl-8 lg:border-l lg:border-white/10">
              <div className="rounded-2xl overflow-hidden aspect-video mb-6 max-w-xl border border-white/10 shadow-dark">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`}
                  title="Azura Mobility"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="font-exo text-white/60 text-lg leading-relaxed max-w-xl">
                Africa has an opportunity no other continent has had: to build a modern economy without the environmental damage that came with industrialisation elsewhere. Azura is designed to prove it, and to profit from it.
              </p>
            </div>*/}
          </div>

        </div>
      </section>

      {/* ══ SERVICES ══ */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div ref={svcRef} className={`flex flex-col sm:flex-row sm:items-end justify-between mb-14 border-b border-border pb-6 transition-all duration-700 ${svcInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center -space-x-1">
                <div className="w-3 h-3 rounded-full bg-accent-amber" />
                <div className="w-3 h-3 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/50">What We Do</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-bg-dark tracking-tight">
              Five revenue engines. <span className="text-accent-amber italic">One integrated platform.</span>
            </h2>
          </div>
          <Link to="/ecosystem" className="mt-4 sm:mt-0 inline-flex items-center gap-2 bg-bg-dark text-white px-5 py-2.5 rounded-full text-xs font-figtree font-bold tracking-wider uppercase hover:bg-accent-amber hover:text-bg-dark transition-all shrink-0 self-start sm:self-auto shadow-capsule">
            Learn More <ArrowRight size={13} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <div key={s.num}
              className={`relative p-7 bg-white border border-border rounded-2xl shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 group flex flex-col justify-between overflow-hidden ${svcInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 75}ms` }}>
              <span className="absolute top-4 right-5 font-figtree font-black text-[3.5rem] leading-none text-neutral-100 select-none pointer-events-none">{s.num}</span>
              <div className="relative z-10">
                <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-border flex items-center justify-center text-bg-dark mb-5 group-hover:bg-accent-amber group-hover:border-accent-amber transition-all duration-300">
                  {s.icon}
                </div>
                <h3 className="text-base font-figtree font-black text-bg-dark tracking-tight mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-neutral-500 font-exo ">{s.desc}</p>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-4 mt-4 relative z-10">
                <Link to={s.link} className="inline-flex items-center gap-1.5 text-xs font-figtree font-bold text-bg-dark group-hover:text-accent-amber transition-colors">
                  Learn more <ArrowRight size={11} />
                </Link>
                <div className="flex items-center -space-x-0.5 opacity-40 group-hover:opacity-80 transition-opacity">
                  <div className="w-2 h-2 rounded-full bg-accent-amber" />
                  <div className="w-2 h-2 rounded-full bg-bg-dark" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ MID-PAGE CTA ══ */}
      <section className="px-6">
        <div className="max-w-7xl mx-auto rounded-3xl bg-bg-dark px-8 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6 border-4 border-accent-amber">
          <div className="text-center md:text-left">
            <p className="font-figtree font-black text-2xl md:text-3xl text-white tracking-tight">
              AZT launches <span className="text-accent-amber italic">October 15.</span>
            </p>
            <p className="font-exo text-white/55 text-sm mt-1">A platform token backed by real revenue, not a promise.</p>
          </div>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm shrink-0">
            Buy AZT <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* ══ PROBLEM ══ */}
      <section className="py-24 px-6 bg-white overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-12 left-10 w-36 h-28 opacity-[0.15]" style={{ background: 'radial-gradient(#14406f 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
          <div className="absolute -bottom-32 -right-32 w-[400px] h-[400px] rounded-full border border-bg-dark/[0.06]" />
          <div className="absolute -bottom-16 -right-16 w-[260px] h-[260px] rounded-full border border-bg-dark/[0.05]" />
          <div className="absolute -top-16 -right-10 w-52 h-52 bg-accent-amber opacity-[0.07]" style={{ borderRadius: '55% 45% 60% 40% / 48% 55% 45% 52%' }} />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bg-dark/10 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Problem</span>
              </div>
              <h2 className="font-figtree font-black text-3xl md:text-5xl text-bg-dark leading-[1.08] tracking-tight max-w-xl">
                Transport challenges<br />we are <span className="text-accent-amber italic">solving</span>
              </h2>
            </div>
            <div className="hidden md:flex flex-col gap-2 opacity-20 pb-2 shrink-0">
              <div className="w-14 h-7 rounded-t-full border-t-2 border-x-2 border-bg-dark" />
              <div className="w-14 h-7 rounded-b-full border-b-2 border-x-2 border-bg-dark" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            {[
              { stat: '30–50%', label: 'Of daily rider income eaten up by fuel', desc: "Nigeria's petrol subsidy removal caused fuel prices to jump 4–6 times over. Eight million riders bear the full cost with no safety net.", num: '01' },
              { stat: '4–8 hrs', label: 'Lost to regular EV charging', desc: 'Standard charging makes running a commercial EV too expensive in Nigeria. Africa needs a system that doesn’t depend on the power grid.', num: '02' },
              { stat: '25M', label: 'Motorcycles in Africa, built nowhere on the continent', desc: 'Every motorcycle purchase sends money out of the country. Africa has the demand, population, and money. It only lacks the will to manufacture locally.', num: '03' },
            ].map(p => (
              <div key={p.num} className="group relative bg-white border border-border rounded-2xl p-8 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col">
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                <span className="absolute bottom-5 right-5 font-figtree font-black text-[5rem] leading-none text-bg-dark/[0.04] select-none pointer-events-none">{p.num}</span>
                <div className="mb-5">
                  <p className="font-figtree font-black text-3xl text-bg-dark leading-none tracking-tight mb-3">{p.stat}</p>
                  <div className="flex items-center gap-2">
                    <div className="h-px w-8 bg-accent-amber" />
                    <p className="font-figtree font-bold text-sm text-bg-dark/70 leading-snug">{p.label}</p>
                  </div>
                </div>
                <div className="border-t border-border mb-5" />
                <p className="font-exo text-sm text-neutral-500 leading-relaxed flex-1">{p.desc}</p>
                <div className="flex items-center -space-x-1 mt-6 opacity-30 group-hover:opacity-60 transition-opacity">
                  <div className="w-2.5 h-2.5 rounded-full bg-accent-amber" />
                  <div className="w-2.5 h-2.5 rounded-full bg-bg-dark" />
                </div>
              </div>
            ))}
          </div>
{/* 
          <div className="grid grid-cols-1 lg:grid-cols-12 overflow-hidden rounded-2xl border border-border shadow-card">
            <div className="lg:col-span-8 bg-white px-10 py-10 flex items-center gap-6">
              <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
              <div> 
                <p className="font-exo text-bg-dark text-base md:text-lg leading-relaxed">
                  These problems are not separate. They feed into each other.{' '}
                  <span className="font-bold">Azura solves all of them at once through one connected network.</span>
                </p>
              </div>
            </div>
            <div className="lg:col-span-4 relative min-h-[200px] bg-bg-dark overflow-hidden">
              <img src="/images/problem-city.jpg" alt="African city" className="absolute inset-0 w-full h-full object-cover opacity-40"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              <div className="absolute inset-0 bg-gradient-to-br from-bg-dark via-bg-dark/80 to-accent-amber/20" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <p className="font-figtree font-black text-5xl text-white leading-none mb-2">8M+</p>
                <p className="text-xs text-white/70 uppercase tracking-[0.18em]">Commercial Riders<br />in Nigeria</p>
                <div className="flex gap-1.5 mt-5 opacity-60">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="rounded-full bg-accent-amber" style={{ width: i === 2 ? 8 : i === 1 || i === 3 ? 6 : 4, height: i === 2 ? 8 : i === 1 || i === 3 ? 6 : 4 }} />
                  ))}
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </section>

      <section className="bg-bg-dark text-white py-24 px-6 border-t-4 border-accent-amber">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5 flex justify-center relative">
            <div className="absolute w-[88%] aspect-square bg-bg-cream rounded-full top-4 left-5 opacity-80" />
            <div className="relative w-[92%] z-10 rounded-full overflow-hidden border-4 border-bg-dark aspect-square shadow-dark bg-bg-cream">
              <img src="/images/bg1.jpg" alt="Azura Fleet" className="w-full h-full object-cover" />
              
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="lg:col-span-7">
            <motion.span variants={fadeUp} className="text-xs font-bold text-accent-amber uppercase tracking-[0.2em] block mb-3">How It Works</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-figtree font-black text-white tracking-tight mb-3">
              Simple. Proven. <span className="text-accent-amber italic">Scalable.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-neutral-400 font-exo text-base leading-relaxed mb-10 max-w-xl">
              From rider onboarding to revenue distribution: the Azura model in four steps.
            </motion.p>

            <motion.div variants={fadeUp} className="relative">
              <div className="hidden sm:block absolute top-5 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-accent-amber/25 to-transparent" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {[
                  { num: '01', title: 'Rider Onboarding', desc: 'A verified rider receives an EV and starts working under the Azura fleet programme.' },
                  { num: '02', title: 'Daily Operations', desc: 'Rider earns income and swaps batteries at Azura stations in under two minutes.' },
                  { num: '03', title: 'Daily Payment', desc: '$6 collected daily by digital payment, with real-time tracking on every transaction.' },
                  { num: '04', title: 'Revenue Allocation', desc: 'Income is shared across operations, expansion, reducing the AZT token supply, and investor returns.' },
                ].map((s, i) => (
                  <div key={s.num} className="text-center" style={{ transitionDelay: `${i * 120}ms` }}>
                    <div className="w-10 h-10 rounded-full bg-accent-amber/10 border border-accent-amber/30 flex items-center justify-center mx-auto mb-4 font-bold text-accent-amber text-xs relative z-10">
                      {s.num}
                    </div>
                    <h3 className="font-figtree font-bold text-base text-white mb-2">{s.title}</h3>
                    <p className="text-gray-300 text-sm font-exo leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-5 mt-10 pt-8 border-t border-white/10">
              <Link to="/ecosystem" className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all">
                See More <ArrowRight size={13} />
              </Link>
            
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══ TICKER 2 ══ */}
      <div className="w-full bg-bg-dark py-3.5 overflow-hidden whitespace-nowrap border-y border-bg-dark-mid">
        <div className="flex animate-ticker">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="flex items-center mx-12 text-xs font-figtree font-bold tracking-[0.18em] text-white/70 uppercase">
              {item} <span className="mx-5 text-accent-amber/40">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══ WHY AZURA ══ */}
      <section className="py-24 px-6 bg-neutral-50">
        <div ref={advRef} className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-dark border-2 border-border group">
              <img src="/images/bike7.png" alt="Azura Mobility"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                 />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/60 to-transparent" />
              <div className="absolute bottom-5 left-5 flex gap-2">
                <span className="badge-dark"><div className="w-1.5 h-1.5 rounded-full bg-accent-amber" /> Made for Africa</span>
                <span className="badge-amber">Launching 2026</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {[{ src: '/images/bike4.png', label: 'Battery Swap' }, { src: '/images/bike1.png', label: 'Electric Fleet' }].map(img => (
                <div key={img.label} className="relative rounded-2xl overflow-hidden aspect-video group border border-border shadow-card">
                  <img src={img.src} alt={img.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/50 to-transparent" />
                  <p className="absolute bottom-2 left-3 text-white text-xs tracking-widest uppercase">{img.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <div className={`transition-all duration-700 delay-100 ${advInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <span className="eyebrow">Why Choose Azura</span>
            <h2 className="text-3xl sm:text-4xl font-figtree font-black text-bg-dark tracking-tight mb-4">
              Why Azura wins <span className="text-accent-amber italic">over the competition</span>
            </h2>
            <p className="text-neutral-600 font-exo text-base leading-relaxed mb-8">
              Azura combines electric vehicles, battery swapping, ride-hailing, and local manufacturing into one platform built specifically for Africa.
            </p>
            <div className="space-y-2.5 mb-8">
              {advantages.map((item, i) => (
                <div key={i}
                  className={`flex items-start gap-3 p-4 rounded-2xl bg-white border border-border shadow-card hover:border-bg-dark/30 hover:shadow-card-hover transition-all duration-200 ${advInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${100 + i * 65}ms` }}>
                  <CheckCircle2 size={16} className="text-bg-dark shrink-0 mt-0.5" />
                  <span className="text-base font-exo text-neutral-600 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
            <Link to="/why-africa" className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule">
              Learn More <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* ══ CTA 2 ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-white tracking-tight mb-4">
            This is bigger than a bike. <span className="text-accent-amber italic">Join the vision.</span>
          </h2>
          <p className="font-exo text-white/70 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            AZT gives you a direct stake in every part of the Azura story, from the first fleet to the first factory.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
            Join the Vision <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* ══ MARKET VALIDATION ══ */}
{/*       <section className="py-24 px-6 bg-white border-t border-border overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-10 left-8 w-40 h-28 opacity-[0.12]" style={{ background: 'radial-gradient(#14406f 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-accent-amber opacity-[0.06]" style={{ borderRadius: '50% 60% 45% 55% / 55% 45% 60% 40%' }} />
          <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full border border-bg-dark/[0.05]" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bg-dark/10 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Market Validation</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight leading-[1.08]">
                The market is <span className="text-accent-amber italic">already moving.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pb-2">
              <p className="font-exo text-base text-neutral-500 leading-relaxed border-l-2 border-accent-amber pl-4">
                Independent data confirming the trends Azura is built to take advantage of: across state governments, market reports, and national policy frameworks.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {validations.map((v, i) => (
              <div key={i} className="group relative bg-white border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col">
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                <span className="absolute bottom-4 right-5 font-figtree font-black text-[4.5rem] leading-none text-bg-dark/[0.04] select-none pointer-events-none">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-serif text-4xl text-accent-amber/20 leading-none mb-3 select-none block">"</span>
                <p className="font-exo text-neutral-600 text-base leading-relaxed flex-1 mb-6">{v.text}</p>
                <div className="flex items-center gap-2.5 pt-4 border-t border-border">
                  <div className="w-7 h-7 rounded-xl bg-accent-amber/10 border border-accent-amber/20 flex items-center justify-center text-bg-dark shrink-0 group-hover:bg-accent-amber group-hover:border-accent-amber transition-all duration-300">
                    {v.icon}
                  </div>
                  <div>
                    <p className="font-figtree font-bold text-xs text-bg-dark leading-none">{v.source}</p>
                    <div className="flex items-center -space-x-0.5 mt-1.5 opacity-30">
                      <div className="w-2 h-2 rounded-full bg-accent-amber" />
                      <div className="w-2 h-2 rounded-full bg-bg-dark" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-bg-dark rounded-2xl overflow-hidden grid grid-cols-2 md:grid-cols-4">
            {marketNums.map((m, i) => (
              <div key={m.l} className="group relative px-8 py-8 border-r border-white/[0.07] last:border-r-0 border-b border-white/[0.07] md:border-b-0 hover:bg-white/[0.04] transition-all duration-300">
                <span className="absolute top-4 right-4 font-figtree font-black text-3xl leading-none text-white/[0.04] select-none pointer-events-none">{String(i + 1).padStart(2, '0')}</span>
                <p className="font-figtree font-black text-3xl md:text-4xl text-accent-amber mb-1.5 tracking-tight">{m.v}</p>
                <p className="text-xs text-white/70 uppercase tracking-[0.15em] leading-snug">{m.l}</p>
                <div className="mt-5 h-px w-0 bg-accent-amber group-hover:w-10 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section> */}
      {/* ══ BLOG / NEWS ══ */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div ref={blogRef} className={`flex flex-col sm:flex-row sm:items-end justify-between mb-14 border-b border-border pb-6 transition-all duration-700 ${blogInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center -space-x-1">
                <div className="w-3 h-3 rounded-full bg-accent-amber" />
                <div className="w-3 h-3 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/50">From the Blog</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-bg-dark tracking-tight">
              Latest <span className="text-accent-amber italic">Updates</span>
            </h2>
          </div>
          <Link to="/news" className="mt-4 sm:mt-0 inline-flex items-center gap-2 bg-bg-dark text-white px-5 py-2.5 rounded-full text-xs font-figtree font-bold tracking-wider uppercase hover:bg-accent-amber hover:text-bg-dark transition-all shrink-0 self-start sm:self-auto shadow-capsule">
            View All Articles <ArrowRight size={13} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featuredPosts.map((post, i) => (
            <Link key={post.slug} to={`/news/${post.slug}`}
              className={`group flex flex-col bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 ${blogInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 75}ms` }}>
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/40 to-transparent" />
                <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-bg-dark text-xs font-figtree font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
                  {post.category}
                </span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-base font-figtree font-black text-bg-dark tracking-tight mb-2 leading-snug group-hover:text-accent-amber transition-colors">{post.title}</h3>
                <p className="text-sm text-neutral-500 font-exo leading-relaxed mb-5 flex-1">{post.excerpt}</p>
                <div className="flex items-center justify-between border-t border-border pt-4">
                  <span className="text-xs text-neutral-400 font-exo flex items-center gap-1.5"><Clock size={12} />{post.readTime}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-figtree font-bold text-bg-dark group-hover:text-accent-amber transition-colors">
                    Read <ArrowRight size={11} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="relative py-28 px-6 overflow-hidden bg-bg-dark">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
        <div className="absolute -bottom-14 -left-14 w-56 h-56 rounded-full border border-white/[0.04]" />
        <div className="absolute inset-0">
          <img src="/images/cta-bg.jpg" alt="" className="w-full h-full object-cover opacity-10" />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-accent-amber/5 rounded-full blur-3xl pointer-events-none" />

        <motion.div className="max-w-2xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.65, ease: 'easeOut' }}>
          <span className="text-xs font-bold text-accent-amber uppercase tracking-[0.2em] block mb-5">Join the Revolution</span>
          <h2 className="text-4xl md:text-5xl font-figtree font-black text-white mb-5 leading-tight tracking-tight">
            Driving Africa Forward.<br />
            <span className="text-accent-amber italic">Electrically.</span>
          </h2>
          <p className="text-neutral-400 font-exo text-base leading-relaxed mb-10 max-w-lg mx-auto">
            Africa's transportation transformation is underway. Azura is built to lead it: from Ibadan to the entire continent.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/invest" className="inline-flex items-center gap-2.5 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white hover:text-bg-dark transition-all ">
             Invest Now<ArrowRight size={13} />
            </Link>{/* 
            <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer" className="btn-outline-white">
              View White Paper ↗
            </a> */}
          </div>
          <p className="text-neutral-500 font-exo text-sm mt-8">
            Prefer not to use crypto? Reach us directly at{' '}
            <a href="mailto:invest@azuramobility.com" className="text-accent-amber hover:text-white transition-colors">invest@azuramobility.com</a>
            {' '}or on{' '}
            <a href="https://t.me/azuramobility" target="_blank" rel="noreferrer" className="text-accent-amber hover:text-white transition-colors">Telegram</a>.
          </p>
        </motion.div>
      </section>

      

      {/* ══ WHITELIST + WHITEPAPER ══ */}
      <WhitelistForm />


    </div>
  );
};

export default Home;